/**
 * WebAuthn credential management and PIN storage for biometric unlock.
 *
 * Uses the Web Authentication API for biometric verification and Web Crypto
 * for encrypting the PIN at rest in IndexedDB. The CryptoKey is non-extractable,
 * binding it to the origin.
 *
 * Pattern: Research Pattern 6 (recommended simpler approach) -- WebAuthn for
 * biometric gating, separate encrypted PIN storage for mnemonic decryption.
 *
 * SECURITY:
 * - PIN is NEVER stored in plaintext
 * - AES-GCM encryption with non-extractable CryptoKey
 * - CryptoKey bound to origin (cannot be exported or moved)
 * - WebAuthn userVerification: "required" ensures biometric prompt
 * - Fresh IV per encryption operation
 *
 * @module webauthn
 */

import {
  DB_NAME,
  PIN_STORAGE_KEY,
  CRYPTO_KEY_STORAGE_KEY,
  CREDENTIAL_ID_KEY,
  openBiometricDb,
  idbPut,
  idbGet,
} from './webauthn-storage';

/** IV length in bytes for AES-GCM. */
const IV_LENGTH = 12;

/**
 * Check whether the WebAuthn API is available on this device.
 *
 * @returns true if WebAuthn with platform authenticator is available
 */
export async function isWebAuthnAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('PublicKeyCredential' in window)) return false;

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Create a WebAuthn platform credential (biometric enrollment).
 *
 * Prompts the user for biometric verification and creates a credential
 * bound to this device. The credential ID should be stored for future
 * assertion calls.
 *
 * @returns The created PublicKeyCredential
 * @throws Error if WebAuthn is unavailable or user cancels
 */
export async function createBiometricCredential(): Promise<PublicKeyCredential> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = crypto.getRandomValues(new Uint8Array(16));

  const options: PublicKeyCredentialCreationOptions = {
    challenge,
    rp: {
      name: 'Satsflow Wallet',
      id: location.hostname,
    },
    user: {
      id: userId,
      name: 'satsflow-user',
      displayName: 'Satsflow User',
    },
    pubKeyCredParams: [
      { alg: -7, type: 'public-key' }, // ES256
      { alg: -257, type: 'public-key' }, // RS256
    ],
    authenticatorSelection: {
      authenticatorAttachment: 'platform',
      userVerification: 'required',
      residentKey: 'preferred',
    },
    timeout: 60000,
  };

  const credential = await navigator.credentials.create({
    publicKey: options,
  });

  if (!credential) {
    throw new Error('Biometric credential creation failed');
  }

  return credential as PublicKeyCredential;
}

/**
 * Assert a WebAuthn credential (biometric verification).
 *
 * Prompts the user for biometric authentication using an existing credential.
 *
 * @param credentialId - The raw credential ID from a previously created credential
 * @returns true if assertion succeeds (biometric verified)
 */
export async function assertBiometric(credentialId: ArrayBuffer): Promise<boolean> {
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));

    const options: PublicKeyCredentialRequestOptions = {
      challenge,
      allowCredentials: [
        {
          id: credentialId,
          type: 'public-key',
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60000,
    };

    const assertion = await navigator.credentials.get({
      publicKey: options,
    });

    return assertion !== null;
  } catch {
    return false;
  }
}

/**
 * Store a PIN encrypted for biometric unlock.
 *
 * Generates a non-extractable AES-GCM CryptoKey, encrypts the PIN,
 * and stores both in IndexedDB. The CryptoKey is origin-bound and
 * cannot be exported.
 *
 * @param pin - The user's PIN to store (will be encrypted)
 */
export async function storePinForBiometric(pin: string): Promise<void> {
  if (!pin || pin.length === 0) {
    throw new Error('PIN must not be empty');
  }

  const cryptoKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );

  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encoder = new TextEncoder();
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encoder.encode(pin),
  );

  const db = await openBiometricDb();

  try {
    // Store the IV alongside the ciphertext
    const blob = { iv: Array.from(iv), data: Array.from(new Uint8Array(encrypted)) };
    await idbPut(db, PIN_STORAGE_KEY, blob);
    await idbPut(db, CRYPTO_KEY_STORAGE_KEY, cryptoKey);
  } finally {
    db.close();
  }
}

/**
 * Retrieve the PIN by decrypting it from IndexedDB.
 *
 * Reads the encrypted PIN blob and CryptoKey from IndexedDB,
 * decrypts the PIN, and returns it.
 *
 * @returns The decrypted PIN string
 * @throws Error if no biometric data is stored or decryption fails
 */
export async function retrievePinViaBiometric(): Promise<string> {
  const db = await openBiometricDb();

  try {
    const blob = await idbGet<{ iv: number[]; data: number[] }>(db, PIN_STORAGE_KEY);
    const cryptoKey = await idbGet<CryptoKey>(db, CRYPTO_KEY_STORAGE_KEY);

    if (!blob || !cryptoKey) {
      throw new Error('No biometric data found');
    }

    const iv = new Uint8Array(blob.iv);
    const data = new Uint8Array(blob.data);

    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } finally {
    db.close();
  }
}

/**
 * Remove all biometric data from IndexedDB.
 *
 * Clears the encrypted PIN, CryptoKey, and credential ID.
 * Called when biometric auth is disabled or on wallet reset.
 */
export async function removeBiometricData(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);

    request.onsuccess = (): void => {
      resolve();
    };
    request.onerror = (): void => {
      reject(new Error('Failed to remove biometric data'));
    };
  });
}

/**
 * Store a credential ID in IndexedDB for later assertion.
 *
 * @param credentialId - Raw credential ID as ArrayBuffer
 */
export async function storeCredentialId(credentialId: ArrayBuffer): Promise<void> {
  const db = await openBiometricDb();
  try {
    await idbPut(db, CREDENTIAL_ID_KEY, Array.from(new Uint8Array(credentialId)));
  } finally {
    db.close();
  }
}

/**
 * Retrieve the stored credential ID from IndexedDB.
 *
 * @returns The credential ID as ArrayBuffer, or null if not found
 */
export async function getStoredCredentialId(): Promise<ArrayBuffer | null> {
  const db = await openBiometricDb();
  try {
    const stored = await idbGet<number[]>(db, CREDENTIAL_ID_KEY);
    if (!stored) return null;
    return new Uint8Array(stored).buffer;
  } finally {
    db.close();
  }
}
